import type { BroadcastContent, TrainInfoLineEntry, TrainInfoProperties } from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import type { ValidationIssue } from '../../../utils/validationIssues';
import { hasIssueForTestId } from '../../../utils/validationIssues';

import { AudioPreviewField } from '../parts/AudioPreviewField';
import { IStationsLinesSelect } from '../parts/IStationsLinesSelect';
import type { PreviewSsmlHandler } from '../types';

// ===========================================================================
// 運行情報 (`train_info`) の UI
// ===========================================================================
//
// 遅延判定を一切行わず、選んだアイステイションズ路線の運行情報 (TrainInfoText) の
// 原文のみを放送するカテゴリ。設定項目は「運行情報と連携」の路線の複数選択だけ。
//
// 【表示方針】
// - 画面には「運行情報と連携」のカード 1 枚だけを出す。以前あった「路線のまとまり」
//   (ブロックの追加 / 並べ替え / 削除 / タブ) は運用上不要なので UI から外した。
// - 保存形式 (`properties.lines[0].istations_lines`) は API / 放送側との互換のため変えない。
//   2 件目以降のブロックは表示せず、路線を編集して保存すると先頭のブロックだけが残る。
//
// 旧「その他」モード (接続線遅延(乗換駅別) の `transfer_stop_mode: 'other'`) を
// 独立カテゴリへ昇格したもので、`IStationsLinesSelect` を再利用する。

/** 見出し横の ⓘ に出す説明文。 */
const TRAIN_INFO_DESCRIPTION =
  '選んだ路線で配信中の運行情報を、そのまま読み上げます。配信されている運行情報が無いときは放送しません。接続線遅延とは独立しています。';

/** 「運行情報と連携」カードの testid 接頭辞 (バリデーションの遷移先と一致させる)。 */
const LINES_TEST_ID_PREFIX = 'train-info-line-istations-lines-0';

/**
 * 運行情報 (`train_info`) のコンテンツ全体のフィールド。
 * 運行情報と連携する路線 (アイステイションズ路線) の複数選択だけを編集する。
 */
export function TrainInfoFields({
  props,
  istationsLines = [],
  issues = [],
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  onChange,
}: {
  props: Partial<TrainInfoProperties>;
  /**
   * 「運行情報と連携」(アイステイションズ路線) の選択肢マスタ。
   * ATOS 線区マスタとは独立した別系統で、連動させない。
   */
  istationsLines?: IStationsLine[];
  /** このコンテンツに紐づく入力エラー (親で `contentIndex` 絞り込み済み)。 */
  issues?: ValidationIssue[];
  /** 試聴対象のコンテンツ全体 (カテゴリ + properties)。未指定なら試聴ボタンを出さない。 */
  content?: BroadcastContent;
  contentIndex?: number;
  parentVoiceId?: string;
  playbackRate?: number;
  onPreviewSsml?: PreviewSsmlHandler;
  onChange: (props: TrainInfoProperties) => void;
}): React.JSX.Element {
  const lines: TrainInfoLineEntry[] = Array.isArray(props.lines) ? props.lines : [];
  // 表示・編集するのは先頭の 1 件だけ (未設定なら空のエントリ)。
  const line: TrainInfoLineEntry = lines[0] ?? {};

  return (
    <div className="contents-fields train-info-fields" data-testid="train-info-fields">
      <div
        className={
          hasIssueForTestId(issues, `${LINES_TEST_ID_PREFIX}-add-select`) ? 'is-invalid' : undefined
        }
      >
        <IStationsLinesSelect
          testIdPrefix={LINES_TEST_ID_PREFIX}
          options={istationsLines}
          value={Array.isArray(line.istations_lines) ? line.istations_lines : []}
          required
          description={TRAIN_INFO_DESCRIPTION}
          onChange={(nextLines) =>
            // 空配列は「未設定」として落とし、DB / JSON にゴミを残さない。
            // 2 件目以降のブロックは持ち越さず、先頭の 1 件だけを保存する。
            onChange({
              ...props,
              lines: [
                {
                  ...line,
                  istations_lines: nextLines.length > 0 ? nextLines : undefined,
                },
              ],
            } as TrainInfoProperties)
          }
        />
      </div>

      {/*
        試聴は保存済みの文面ではなく、押した時点で配信中の運行情報を取得して読み上げる
        (本番と同じ規則)。設定した路線に配信が無いと 400 になり、エラーとして表示される。
        文面を固定値にしないので、プレースホルダやサンプル条件は持たない。
      */}
      {content ? (
        <AudioPreviewField
          content={content}
          parentVoiceId={parentVoiceId}
          playbackRate={playbackRate}
          onPreviewSsml={onPreviewSsml}
          testIdSuffix={`train-info-${contentIndex}`}
          label="▶ 配信中の運行情報を試聴"
          disabled={!Array.isArray(line.istations_lines) || line.istations_lines.length === 0}
          disabledReason="運行情報と連携する路線を選ぶと試聴できます"
        />
      ) : null}
    </div>
  );
}
